import { 
  collection, 
  collectionGroup,
  doc, 
  getDocs,
  getDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  type QueryConstraint,
  Timestamp,
  serverTimestamp,
  addDoc
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { httpsCallable, getFunctions } from 'firebase/functions';

export interface User {
  id: string;
  email: string;
  displayName?: string;
  photoURL?: string;
  role: 'bevpass_admin' | 'merchant_owner' | 'merchant_viewer' | 'customer';
  merchantIds?: string[];
  status: 'active' | 'inactive' | 'suspended' | 'pending' | 'pending_approval' | 'rejected';
  createdAt: Date;
  lastActive?: Date;
  subscriptionCount?: number;
  phoneNumber?: string;
  metadata?: {
    lastLogin?: Date;
    loginCount?: number;
    deviceInfo?: string;
    ipAddress?: string;
  };
}

export interface AdminUser {
  email: string;
  displayName?: string;
  role: 'bevpass_admin' | 'merchant_owner' | 'merchant_viewer';
  merchantIds?: string[];
  permissions?: string[];
  isPublicRegistration?: boolean;
  businessName?: string;
  businessDescription?: string;
}

export interface UserActivity {
  id: string;
  userId: string;
  action: string;
  details?: Record<string, unknown>;
  timestamp: Date;
  ipAddress?: string;
  userAgent?: string;
}

export interface UserStats {
  totalUsers: number;
  activeUsers: number;
  merchantAccounts: number;
  suspendedUsers: number;
  newUsersThisMonth: number;
  usersByRole: {
    bevpass_admin: number;
    merchant_owner: number;
    merchant_viewer: number;
    customer: number;
  };
}

export interface UserSubscription {
  id: string;
  merchantId: string;
  merchantName?: string;
  planId: string;
  planName?: string;
  status: string;
  amount: number;
  currency: string;
  interval: string;
  currentPeriodStart?: Date;
  currentPeriodEnd?: Date;
  createdAt?: Date;
}

export interface UserRedemption {
  id: string;
  merchantId: string;
  merchantName?: string;
  locationId: string;
  locationName?: string;
  planId?: string;
  planName?: string;
  redeemedAt: Date;
  method: 'barcode' | 'manual';
  status: 'completed' | 'pending' | 'failed';
}

// Helper function to sanitize user data from Firestore
const getErrorMessage = (error: unknown, fallback: string) =>
  error instanceof Error ? error.message || fallback : fallback;

const sanitizeUserData = (id: string, data: Record<string, unknown>): User => {
  // Log missing fields in development
  if (process.env.NODE_ENV === 'development') {
    if (!data.role) console.warn(`User ${id} missing role field`);
    if (!data.status) console.warn(`User ${id} missing status field`);
    if (!data.email) console.warn(`User ${id} missing email field`);
  }

  return {
    id,
    email: (data.email as string | undefined) || `user_${id}@bevpass.com`,
    displayName: data.displayName as string | undefined,
    photoURL: data.photoURL as string | undefined,
    role: (data.role as User['role']) || 'customer',
    merchantIds: Array.isArray(data.merchantIds) ? (data.merchantIds as string[]) : [],
    status: (data.status as User['status']) || 'active',
    createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date(),
    lastActive: data.lastActive instanceof Timestamp ? data.lastActive.toDate() : undefined,
    subscriptionCount: typeof data.subscriptionCount === "number" ? data.subscriptionCount : 0,
    phoneNumber: data.phoneNumber as string | undefined,
    metadata: (data.metadata as User['metadata']) || undefined,
  };
};

class UsersService {
  private functions = getFunctions();
  
  // Get all users with optional filters
  async getUsers(filters?: {
    role?: string;
    status?: string;
    merchantId?: string;
    searchQuery?: string;
    limit?: number;
  }): Promise<User[]> {
    try {
      const constraints: QueryConstraint[] = [];
      
      if (filters?.role && filters.role !== 'all') {
        constraints.push(where('role', '==', filters.role));
      }
      
      if (filters?.status && filters.status !== 'all') {
        if (filters.status === 'pending') {
          // Include both pending states
          constraints.push(where('status', 'in', ['pending', 'pending_approval']));
        } else {
          constraints.push(where('status', '==', filters.status));
        }
      }
      
      if (filters?.merchantId) {
        constraints.push(where('merchantIds', 'array-contains', filters.merchantId));
      }
      
      constraints.push(orderBy('createdAt', 'desc'));
      
      if (filters?.limit) {
        constraints.push(limit(filters.limit));
      }
      
      const usersQuery = query(collection(db, 'users'), ...constraints);
      const snapshot = await getDocs(usersQuery);
      
      const users = snapshot.docs.map(doc =>
        sanitizeUserData(doc.id, doc.data())
      );
      
      // Client-side search filtering
      if (filters?.searchQuery) {
        const searchLower = filters.searchQuery.toLowerCase();
        return users.filter(user =>
          user.email.toLowerCase().includes(searchLower) ||
          user.displayName?.toLowerCase().includes(searchLower)
        );
      }
      
      return users;
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  }
  
  // Get users related to a specific merchant (subscribers + admins)
  async getUsersByMerchantContext(merchantId: string, userType: 'all' | 'subscribers' | 'admins' = 'all'): Promise<User[]> {
    try {
      const usersMap = new Map<string, User>();

      const addUser = (user: User) => {
        if (!usersMap.has(user.id)) {
          usersMap.set(user.id, user);
        }
      };

      if (userType === 'all' || userType === 'admins') {
        const adminUsersQuery = query(
          collection(db, 'users'),
          where('merchantIds', 'array-contains', merchantId)
        );
        const adminSnapshot = await getDocs(adminUsersQuery);
        adminSnapshot.docs.forEach(docSnap => addUser(sanitizeUserData(docSnap.id, docSnap.data())));
      }

      if (userType === 'all' || userType === 'subscribers') {
        const subscriptionsQuery = query(
          collectionGroup(db, 'subscriptions'),
          where('merchantId', '==', merchantId),
          where('status', '==', 'active')
        );
        const subscriptionsSnapshot = await getDocs(subscriptionsQuery);

        const subscriberIds = new Set<string>();
        subscriptionsSnapshot.docs.forEach(docSnap => {
          const userId = docSnap.ref.parent.parent?.id;
          if (userId) {
            subscriberIds.add(userId);
          }
        });

        for (const subscriberId of subscriberIds) {
          try {
            const userDoc = await getDoc(doc(db, 'users', subscriberId));
            if (userDoc.exists()) {
              addUser(sanitizeUserData(userDoc.id, userDoc.data()));
            }
          } catch (error) {
            console.warn(`Failed to fetch subscriber ${subscriberId}:`, error);
          }
        }
      }

      return Array.from(usersMap.values()).sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());

    } catch (error) {
      console.error('Error fetching users by merchant context:', error);
      throw error;
    }
  }
  
  // Get user by ID
  async getUserById(userId: string): Promise<User | null> {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      
      if (!userDoc.exists()) {
        return null;
      }
      
      return sanitizeUserData(userDoc.id, userDoc.data());
    } catch (error) {
      console.error('Error fetching user:', error);
      throw error;
    }
  }
  
  // Update user details
  async updateUser(userId: string, updates: Partial<User>): Promise<void> {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        ...updates,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  }
  
  // Create or invite admin user (Cloud Function handles everything)
  async createAdminUser(adminUser: AdminUser): Promise<{ success: boolean; userId?: string; message?: string; error?: string; emailSent?: boolean; requiresApproval?: boolean; signInLink?: string }> {
    try {
      // Cloud Function now handles both user creation and registration approval
      const createAdmin = httpsCallable(this.functions, 'createAdminUserV2');
      // Sanitize payload: default booleans and remove undefined to avoid Firestore undefined errors server-side
      const isPublicRegistration = adminUser.isPublicRegistration ?? false;
      const payload: Record<string, unknown> = {
        ...adminUser,
        isPublicRegistration,
      };

      Object.keys(payload).forEach((key) => {
        if (payload[key] === undefined) {
          delete payload[key];
        }
      });

      const result = await createAdmin(payload);
      const data = result.data as { success: boolean; userId?: string; message?: string; error?: string; emailSent?: boolean; requiresApproval?: boolean; signInLink?: string };

      const email = typeof adminUser.email === 'string' ? adminUser.email : undefined;
      if (data.success && email && !isPublicRegistration) {
        window.localStorage.setItem('emailForSignIn', email);
      }
      
      return data;
    } catch (error: unknown) {
      console.error('Error creating admin user:', error);
      return { success: false, error: getErrorMessage(error, 'Failed to create admin user') };
    }
  }
  
  // Update user role and permissions (requires Cloud Function)
  async updateUserRole(
    userId: string, 
    role: string, 
    merchantIds?: string[]
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const updateRole = httpsCallable(this.functions, 'updateUserRole');
      const result = await updateRole({ userId, role, merchantIds });
      return result.data as { success: boolean; error?: string };
    } catch (error) {
      console.error('Error updating user role:', error);
      return { success: false, error: 'Failed to update user role' };
    }
  }
  
  // Suspend or reactivate user
  async updateUserStatus(userId: string, status: 'active' | 'inactive' | 'suspended'): Promise<void> {
    try {
      await updateDoc(doc(db, 'users', userId), {
        status,
        statusUpdatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error updating user status:', error);
      throw error;
    }
  }
  
  // Delete user (requires Cloud Function for complete deletion)
  async deleteUser(userId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const deleteUserFunc = httpsCallable(this.functions, 'deleteUser');
      const result = await deleteUserFunc({ userId });
      return result.data as { success: boolean; error?: string };
    } catch (error) {
      console.error('Error deleting user:', error);
      return { success: false, error: 'Failed to delete user' };
    }
  }
  
  // Get user activity logs
  async getUserActivity(userId: string, days: number = 30): Promise<UserActivity[]> {
    try {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);
      
      const activityQuery = query(
        collection(db, 'userActivity'),
        where('userId', '==', userId),
        where('timestamp', '>=', Timestamp.fromDate(startDate)),
        orderBy('timestamp', 'desc'),
        limit(100)
      );
      
      const snapshot = await getDocs(activityQuery);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate() || new Date(),
      } as UserActivity));
    } catch (error) {
      console.error('Error fetching user activity:', error);
      return [];
    }
  }
  
  // Get user statistics
  async getUserStats(): Promise<UserStats> {
    try {
      const users = await this.getUsers();
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      
      const stats: UserStats = {
        totalUsers: users.length,
        activeUsers: users.filter(u => u.status === 'active').length,
        merchantAccounts: users.filter(u => 
          u.role === 'merchant_owner' || u.role === 'merchant_viewer'
        ).length,
        suspendedUsers: users.filter(u => u.status === 'suspended').length,
        newUsersThisMonth: users.filter(u => 
          u.createdAt >= startOfMonth
        ).length,
        usersByRole: {
          bevpass_admin: users.filter(u => u.role === 'bevpass_admin').length,
          merchant_owner: users.filter(u => u.role === 'merchant_owner').length,
          merchant_viewer: users.filter(u => u.role === 'merchant_viewer').length,
          customer: users.filter(u => u.role === 'customer').length,
        }
      };
      
      return stats;
    } catch (error) {
      console.error('Error calculating user stats:', error);
      throw error;
    }
  }
  
  // Send password reset email (requires Cloud Function)
  async sendPasswordReset(email: string): Promise<{ success: boolean; error?: string }> {
    try {
      const sendReset = httpsCallable(this.functions, 'sendPasswordReset');
      const result = await sendReset({ email });
      return result.data as { success: boolean; error?: string };
    } catch (error) {
      console.error('Error sending password reset:', error);
      return { success: false, error: 'Failed to send password reset' };
    }
  }
  
  // Resend invitation email with new sign-in link (via Cloud Function)
  async resendInvitation(userId: string): Promise<{ success: boolean; message?: string; error?: string; emailSent?: boolean; signInLink?: string }> {
    try {
      // Cloud Function now provides sign-in link for manual sharing
      const resendInvite = httpsCallable(this.functions, 'resendInvitationEmail');
      const result = await resendInvite({ userId });
      const data = result.data as { success: boolean; message?: string; error?: string; emailSent?: boolean; signInLink?: string };
      
      // Get user email to store for verification
      if (data.success) {
        const userDoc = await getDoc(doc(db, 'users', userId));
        if (userDoc.exists()) {
          window.localStorage.setItem('emailForSignIn', userDoc.data().email);
        }
      }
      
      return data;
    } catch (error: unknown) {
      console.error('Error resending invitation:', error);
      return { success: false, error: getErrorMessage(error, 'Failed to resend invitation') };
    }
  }

  // Get stored invitation link for a user (from pendingInvitations)
  async getInvitationLink(userId: string): Promise<string | null> {
    try {
      const invitationDoc = await getDoc(doc(db, 'pendingInvitations', userId));
      if (!invitationDoc.exists()) return null;
      const data = invitationDoc.data();
      return (data && typeof data.signInLink === 'string') ? data.signInLink : null;
    } catch (error) {
      console.error('Error fetching invitation link:', error);
      return null;
    }
  }

  // Approve a pending user registration
  async approveUserRegistration(userId: string, merchantIds?: string[]): Promise<{ success: boolean; message?: string; error?: string; signInLink?: string }> {
    try {
      const approveUser = httpsCallable(this.functions, 'approveUserRegistration');
      const result = await approveUser({ userId, merchantIds });
      const data = result.data as { success: boolean; message?: string; error?: string; signInLink?: string };
      
      // Store email for sign-in link verification if successful
      if (data.success) {
        const userDoc = await getDoc(doc(db, 'users', userId));
        if (userDoc.exists()) {
          window.localStorage.setItem('emailForSignIn', userDoc.data().email);
        }
      }
      
      return data;
    } catch (error: unknown) {
      console.error('Error approving user registration:', error);
      return { success: false, error: getErrorMessage(error, 'Failed to approve user registration') };
    }
  }

  // Reject a pending user registration
  async rejectUserRegistration(userId: string, reason?: string): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
      const rejectUser = httpsCallable(this.functions, 'rejectUserRegistration');
      const result = await rejectUser({ userId, reason });
      return result.data as { success: boolean; message?: string; error?: string };
    } catch (error: unknown) {
      console.error('Error rejecting user registration:', error);
      return { success: false, error: getErrorMessage(error, 'Failed to reject user registration') };
    }
  }
  
  // Bulk export users
  async exportUsers(format: 'csv' | 'json' = 'csv'): Promise<string> {
    try {
      const users = await this.getUsers();
      
      if (format === 'json') {
        return JSON.stringify(users, null, 2);
      }
      
      // CSV format
      const headers = ['ID', 'Email', 'Display Name', 'Role', 'Status', 'Created At', 'Last Active'];
      const rows = users.map(user => [
        user.id,
        user.email,
        user.displayName || '',
        user.role,
        user.status,
        user.createdAt.toISOString(),
        user.lastActive?.toISOString() || ''
      ]);
      
      const csv = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');
      
      return csv;
    } catch (error) {
      console.error('Error exporting users:', error);
      throw error;
    }
  }
  
  // Log user activity
  async logActivity(userId: string, action: string, details?: Record<string, unknown>): Promise<void> {
    try {
      await addDoc(collection(db, 'userActivity'), {
        userId,
        action,
        details,
        timestamp: serverTimestamp(),
        userAgent: navigator.userAgent,
      });
    } catch (error) {
      console.error('Error logging activity:', error);
      // Don't throw - logging shouldn't break the app
    }
  }
  
  // Get user's active subscriptions
  async getUserSubscriptions(userId: string): Promise<UserSubscription[]> {
    try {
      // Query the user's subscriptions subcollection
      const subscriptionsQuery = query(
        collection(db, 'users', userId, 'subscriptions'),
        where('status', 'in', ['active', 'trialing', 'past_due'])
      );
      
      const snapshot = await getDocs(subscriptionsQuery);
      const subscriptions: UserSubscription[] = [];
      
      // Process each subscription and fetch merchant/plan details
      for (const docSnapshot of snapshot.docs) {
        const data = docSnapshot.data();
        let merchantName = 'Unknown Merchant';
        let planName = 'Unknown Plan';
        
        // Fetch merchant details
        if (data.merchantId) {
          try {
            const merchantDoc = await getDoc(doc(db, 'merchants', data.merchantId));
            if (merchantDoc.exists()) {
              merchantName = merchantDoc.data().name || merchantName;
            }
          } catch (err) {
            console.warn(`Failed to fetch merchant ${data.merchantId}:`, err);
          }
        }
        
        // Initialize price variables
        let amount = 0;
        let interval = 'month';
        let intervalCount = 1;
        
        // Fetch plan and price details
        if (data.merchantId && data.planId) {
          try {
            // Get the plan details
            const planDoc = await getDoc(
              doc(db, 'merchants', data.merchantId, 'subscriptionPlans', data.planId)
            );
            if (planDoc.exists()) {
              planName = planDoc.data().name || planName;
            }
            
            // Fetch the price from the prices subcollection
            if (data.priceId) {
              const priceDoc = await getDoc(
                doc(db, 'merchants', data.merchantId, 'subscriptionPlans', data.planId, 'prices', data.priceId)
              );
              if (priceDoc.exists()) {
                const priceData = priceDoc.data();
                amount = priceData.amount || 0;
                interval = priceData.interval || 'month';
                intervalCount = priceData.intervalCount || 1;
              }
            } else {
              // If no priceId, try to get the first/default price from the prices collection
              const pricesQuery = query(
                collection(db, 'merchants', data.merchantId, 'subscriptionPlans', data.planId, 'prices'),
                where('active', '==', true),
                limit(1)
              );
              const pricesSnapshot = await getDocs(pricesQuery);
              if (!pricesSnapshot.empty) {
                const priceData = pricesSnapshot.docs[0].data();
                amount = priceData.amount || 0;
                interval = priceData.interval || 'month';
                intervalCount = priceData.intervalCount || 1;
              }
            }
          } catch (err) {
            console.warn(`Failed to fetch plan/price details for ${data.planId}:`, err);
          }
        }
        
        // Fallback to subscription data if we still don't have price info
        if (amount === 0) {
          amount = data.amount || data.priceAmount || data.price || 0;
          interval = data.interval || data.recurring?.interval || interval;
          intervalCount = data.intervalCount || data.recurring?.interval_count || intervalCount;
        }
        
        subscriptions.push({
          id: docSnapshot.id,
          merchantId: data.merchantId || '',
          merchantName,
          planId: data.planId || '',
          planName,
          status: data.status || 'active',
          amount,
          currency: data.currency || 'usd',
          interval,
          currentPeriodStart: data.currentPeriodStart?.toDate ? data.currentPeriodStart.toDate() : undefined,
          currentPeriodEnd: data.currentPeriodEnd?.toDate ? data.currentPeriodEnd.toDate() : undefined,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : undefined,
        });
      }
      
      return subscriptions;
    } catch (error) {
      console.error('Error fetching user subscriptions:', error);
      return [];
    }
  }
  
  // Get user's redemption history
  async getUserRedemptions(userId: string, limitCount: number = 50): Promise<UserRedemption[]> {
    try {
      // Query the user's redemptions subcollection
      const redemptionsQuery = query(
        collection(db, 'users', userId, 'redemptions'),
        orderBy('redeemedAt', 'desc'),
        limit(limitCount)
      );
      
      const snapshot = await getDocs(redemptionsQuery);
      const redemptions: UserRedemption[] = [];
      
      // Process each redemption and fetch related details
      for (const docSnapshot of snapshot.docs) {
        const data = docSnapshot.data();
        let merchantName = 'Unknown Merchant';
        let locationName = 'Unknown Location';
        let planName = data.planName || 'Unknown Plan';
        
        // Fetch merchant details
        if (data.merchantId) {
          try {
            const merchantDoc = await getDoc(doc(db, 'merchants', data.merchantId));
            if (merchantDoc.exists()) {
              merchantName = merchantDoc.data().name || merchantName;
            }
          } catch (err) {
            console.warn(`Failed to fetch merchant ${data.merchantId}:`, err);
          }
        }
        
        // Fetch location details
        if (data.merchantId && data.locationId) {
          try {
            // Try merchant's locations subcollection first
            const locationDoc = await getDoc(
              doc(db, 'merchants', data.merchantId, 'locations', data.locationId)
            );
            if (locationDoc.exists()) {
              locationName = locationDoc.data().name || locationDoc.data().displayName || locationName;
            } else {
              // Try global locations collection as fallback
              const globalLocationDoc = await getDoc(doc(db, 'locations', data.locationId));
              if (globalLocationDoc.exists()) {
                locationName = globalLocationDoc.data().name || globalLocationDoc.data().displayName || locationName;
              }
            }
          } catch (err) {
            console.warn(`Failed to fetch location ${data.locationId}:`, err);
          }
        }
        
        // Fetch plan details if not already present
        if (data.merchantId && data.planId && !data.planName) {
          try {
            const planDoc = await getDoc(
              doc(db, 'merchants', data.merchantId, 'subscriptionPlans', data.planId)
            );
            if (planDoc.exists()) {
              planName = planDoc.data().name || planName;
            }
          } catch (err) {
            console.warn(`Failed to fetch plan ${data.planId}:`, err);
          }
        }
        
        redemptions.push({
          id: docSnapshot.id,
          merchantId: data.merchantId || '',
          merchantName,
          locationId: data.locationId || '',
          locationName,
          planId: data.planId,
          planName,
          redeemedAt: data.redeemedAt?.toDate ? data.redeemedAt.toDate() : new Date(),
          method: data.method || 'barcode',
          status: data.status || 'completed',
        });
      }
      
      return redemptions;
    } catch (error) {
      console.error('Error fetching user redemptions:', error);
      return [];
    }
  }
  
  // Get merchant names for given IDs
  async getMerchantNames(merchantIds: string[]): Promise<Record<string, string>> {
    try {
      const merchantNames: Record<string, string> = {};
      
      // Fetch all merchants in parallel
      const promises = merchantIds.map(async (merchantId) => {
        try {
          const merchantDoc = await getDoc(doc(db, 'merchants', merchantId));
          if (merchantDoc.exists()) {
            merchantNames[merchantId] = merchantDoc.data().name || merchantId;
          } else {
            merchantNames[merchantId] = merchantId;
          }
        } catch (err) {
          console.warn(`Failed to fetch merchant ${merchantId}:`, err);
          merchantNames[merchantId] = merchantId;
        }
      });
      
      await Promise.all(promises);
      return merchantNames;
    } catch (error) {
      console.error('Error fetching merchant names:', error);
      // Return original IDs as fallback
      return merchantIds.reduce((acc, id) => ({ ...acc, [id]: id }), {});
    }
  }
}

export const usersService = new UsersService();
